import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import { local } from '../lib/storage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Download, Trash2, Plus, Users, Upload } from 'lucide-react';
import { INDIAN_STATES } from '../lib/states';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

function parseCSV(text) {
  const rows = [];
  let row = [], cell = '', quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i], next = text[i + 1];
    if (ch === '"' && quoted && next === '"') { cell += '"'; i += 1; }
    else if (ch === '"') quoted = !quoted;
    else if (ch === ',' && !quoted) { row.push(cell.trim()); cell = ''; }
    else if ((ch === '\n' || ch === '\r') && !quoted) {
      if (cell || row.length) { row.push(cell.trim()); rows.push(row); row = []; cell = ''; }
      if (ch === '\r' && next === '\n') i += 1;
    } else cell += ch;
  }
  if (cell || row.length) { row.push(cell.trim()); rows.push(row); }
  return rows.filter((r) => r.some(Boolean));
}

function customersFromCSV(text) {
  const rows = parseCSV(text);
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.toLowerCase().trim());
  const hasHeaders = headers.includes('name') || headers.includes('customer');
  const body = hasHeaders ? rows.slice(1) : rows;
  const idx = (names, fallback) => {
    const found = names.map((n) => headers.indexOf(n)).find((i) => i >= 0);
    return found == null ? fallback : found;
  };
  return body.map((r) => ({
    name: r[idx(['name', 'customer', 'customer name'], 0)] || '',
    gstin: r[idx(['gstin', 'gst'], 1)] || '',
    state: r[idx(['state'], 2)] || '',
    stateName: r[idx(['state name', 'statename'], 3)] || '',
    address: r[idx(['address'], 4)] || '',
    email: r[idx(['email'], 5)] || '',
    phone: r[idx(['phone', 'mobile'], 6)] || '',
  })).filter((c) => c.name);
}

function downloadCSV(filename, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Customers() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ name: '', gstin: '', state: '', stateName: '', address: '', email: '', phone: '' });

  useEffect(() => {
    (async () => {
      if (user) {
        try { const { data } = await apiClient.get('/customers'); setCustomers(data); } catch { setCustomers([]); }
      } else {
        setCustomers(local.getCustomers());
      }
    })();
  }, [user]);

  const add = async () => {
    if (!form.name) { toast.error(`${t('name')} ${t('required')}`); return; }
    const payload = { ...form, id: crypto.randomUUID() };
    if (user) {
      try { const { data } = await apiClient.post('/customers', form); setCustomers((c) => [data, ...c]); toast.success(t('saved')); } catch (e) { toast.error(t('failedToSave')); }
    } else {
      const next = local.addCustomer(payload);
      setCustomers(next);
      toast.success(t('savedLocally'));
    }
    setForm({ name: '', gstin: '', state: '', stateName: '', address: '', email: '', phone: '' });
  };

  const uploadCSV = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const imported = customersFromCSV(await file.text());
    if (!imported.length) { toast.error(t('noCustomersFoundCsv')); return; }
    if (user) {
      const saved = [];
      for (const c of imported) {
        try {
          const { data } = await apiClient.post('/customers', c);
          saved.push(data);
        } catch {}
      }
      setCustomers((list) => [...saved, ...list]);
      toast.success(`${t('imported')} ${saved.length} ${t('customers')}`);
    } else {
      let next = local.getCustomers();
      imported.forEach((c) => { next = local.addCustomer({ ...c, id: crypto.randomUUID() }); });
      setCustomers(next);
      toast.success(`${t('imported')} ${imported.length} ${t('customers')} ${t('locally')}`);
    }
  };

  const downloadTemplate = () => {
    downloadCSV('billjaldi-customers-template.csv', [
      ['name', 'gstin', 'state', 'stateName', 'address', 'email', 'phone'],
      ['Acme Pvt Ltd', '29AAAAA0000A1Z5', 'KA', 'Karnataka', 'MG Road, Bengaluru', 'billing@acme.com', '9876543210'],
    ]);
  };

  const remove = async (c) => {
    if (user) {
      try { await apiClient.delete(`/customers/${c.id}`); } catch {}
    } else {
      local.removeCustomer(c.id);
    }
    setCustomers((list) => list.filter((x) => x.id !== c.id));
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="customers-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t('customers')}</h1>
        <p className="text-sm text-slate-500 mt-1">{user ? t('customersSource') : t('lastTenBrowser')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="customer-form-card">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="font-heading font-bold">{t('addCustomer')}</h3>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50" data-testid="customers-csv-template">
                <Download className="w-3.5 h-3.5" /> {t('template')}
              </button>
              <label className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-md px-2 py-1 cursor-pointer hover:bg-slate-50">
                <Upload className="w-3.5 h-3.5" /> {t('csv')}
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={uploadCSV} data-testid="customers-csv-upload" />
              </label>
            </div>
          </div>
          <div className="space-y-3">
            <div><Label>{t('name')}</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="cust-form-name" /></div>
            <div><Label>{t('gstin')}</Label><Input value={form.gstin} onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))} className="font-mono uppercase" data-testid="cust-form-gstin" /></div>
            <div>
              <Label>{t('state')}</Label>
              <Select value={form.state} onValueChange={(v) => {
                const st = INDIAN_STATES.find((s) => s.short === v);
                setForm((f) => ({ ...f, state: v, stateName: st?.name || '' }));
              }}>
                <SelectTrigger data-testid="cust-form-state"><SelectValue placeholder={t('selectState')} /></SelectTrigger>
                <SelectContent className="max-h-64">{INDIAN_STATES.map((s) => <SelectItem key={s.short} value={s.short}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t('email')}</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="cust-form-email" /></div>
            <div><Label>{t('phone')}</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="cust-form-phone" /></div>
            <div><Label>{t('address')}</Label><Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="cust-form-address" /></div>
            <Button onClick={add} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="cust-form-submit"><Plus className="w-4 h-4 mr-1" /> {t('add')}</Button>
          </div>
        </div>

        <div className="lg:col-span-2">
          {customers.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl" data-testid="customers-empty">
              <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <div className="text-slate-400">{t('noCustomersYet')}</div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="customers-list">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 text-left">
                  <tr><th className="px-4 py-3 font-bold">{t('name')}</th><th className="px-4 py-3 font-bold">{t('gstin')}</th><th className="px-4 py-3 font-bold">{t('phone')}</th><th className="px-4 py-3 font-bold">{t('state')}</th><th className="px-4 py-3" /></tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-semibold">{c.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.gstin || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.phone || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{c.stateName || c.state || '—'}</td>
                      <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`cust-remove-${c.id}`}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
